import React, { Suspense, useState } from "react";
import { Routes, Route, Navigate, Link } from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ToastProvider } from "./contexts/ToastContext";
import { Toaster } from "@/components/ui/sonner";
import FloatingNav from "./components/ui/floating-nav";
import { Footer } from "./components/Footer";
import SupabaseToggle from "./components/SupabaseToggle";
import ProtectedRoute from "./components/ProtectedRoute";
import { PasswordGate } from "./components/PasswordGate";
import { AdminBypass } from "./components/AdminBypass";
import { ScrollToTop } from "./components/ScrollToTop";
import { CookieBanner } from "./components/CookieBanner";
import { Analytics } from '@vercel/analytics/react';

// Import pages
import Index from "./pages/index";
import Register from "./pages/auth/Register";
import Auth from "./pages/auth/Auth";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import Search from "./pages/Search";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import FloristProfile from "./pages/FloristProfile";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import OrderConfirmation from "./pages/OrderConfirmation";
import CheckoutProcessing from "./routes/checkout/processing";
import DeliveryDetails from "./pages/DeliveryDetails";
import { 
  NotificationsPage,
  LiveDashboard,
  FloristOrderDetails,
  StripeTestPage
} from "./pages/florist-dashboard";

// Import the new cart page
import NewCart from "./pages/NewCart";

// Import dashboard components
import { Dashboard } from "./components/florist-dashboard/Dashboard"; 
import StoreSettings from "./pages/StoreSettings";
import FloristAnalytics from "./pages/FloristAnalytics";
import FloristStripe from "./pages/FloristStripe";
// Import the proper Orders and Products pages
import Orders from "./pages/Orders";
import Products from "./pages/Products";
import EditProduct from "./pages/EditProduct";
import NewProduct from "./pages/NewProduct";

// Import admin pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminSettings from "./pages/admin/AdminSettings";
import StoreManagement from "./pages/admin/StoreManagement";
import StoreDetails from "./pages/admin/StoreDetails";
import AdminNewProduct from "./pages/admin/AdminNewProduct";
import AdminEditProduct from "./pages/admin/AdminEditProduct";
import OrderManagement from "./pages/admin/OrderManagement";
import CommissionManagement from "./pages/admin/CommissionManagement";
import UserManagement from "./pages/admin/UserManagement";
import CustomerManagement from "./pages/admin/CustomerManagement";
import FloristManagement from "./pages/admin/FloristManagement";
import CustomerDashboard from "./pages/CustomerDashboard";
import ProfileSettings from "./pages/ProfileSettings";
import OrderDetails from "./pages/OrderDetails";
import PaymentSettings from "./pages/PaymentSettings";
import About from "./pages/About";
import Contact from "./pages/Contact";
import FAQs from "./pages/FAQs";
import Shipping from "./pages/Shipping";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import FeaturedContentManagement from "./pages/admin/FeaturedContentManagement";

import GuestOrderLookup from "./pages/GuestOrderLookup";
import OrderRedirect from "./pages/OrderRedirect";

// SEO Pages
import FlowersSydney from "./pages/seo/FlowersSydney";
import SameDayFlowerDelivery from "./pages/seo/SameDayFlowerDelivery";
import FloristsSydney from "./pages/seo/FloristsSydney";
import LocalFlorists from "./pages/seo/LocalFlorists";
import FlowerDeliverySydney from "./pages/seo/FlowerDeliverySydney";

// Suburb Pages
import FloristsNewtown from "./pages/suburbs/FloristsNewtown";
import FloristsBondi from "./pages/suburbs/FloristsBondi";
import FloristsSurryHills from "./pages/suburbs/FloristsSurryHills";
import FloristsParramatta from "./pages/suburbs/FloristsParramatta";
import FloristsMarrickville from "./pages/suburbs/FloristsMarrickville";

// Blog
import BlogIndex from "./pages/blog/index";
import BlogPost from "./components/seo/BlogPost";

// Add CSS to remove the black bar
const appStyles = {
  backgroundColor: "#FAF7F0",
  minHeight: "100vh",
};

function App() {
  const SITE_PASSWORD = import.meta.env.VITE_SITE_PASSWORD;
  const isGateDisabled = !SITE_PASSWORD;
  const hasGrantedAccess = localStorage.getItem('marketbloom_access') === 'granted';
  const [hasAccess, setHasAccess] = useState(isGateDisabled || hasGrantedAccess);

  if (!hasAccess) {
    return <PasswordGate onPasswordCorrect={() => setHasAccess(true)} />;
  }

  return (
    <ThemeProvider>
      <ToastProvider>
       <div className="app flex flex-col min-h-screen" style={appStyles}>
         <FloatingNav />
         
         {import.meta.env.DEV && <div className="fixed top-4 right-4 z-50 opacity-50 hover:opacity-100"><SupabaseToggle /></div>}
         
         <main className="flex-grow pt-0">
           <ScrollToTop />
           <Suspense fallback={<div className="flex justify-center items-center h-screen"><p>Loading page...</p></div>}>
             <Routes>
               <Route path="/" element={<Index />} />
               <Route path="/auth" element={<Auth />} />
               <Route path="/login" element={<Navigate to="/auth" replace />} />
               <Route path="/register" element={<Register />} />
               <Route path="/forgot-password" element={<ForgotPassword />} />
               <Route path="/reset-password" element={<ResetPassword />} />
               <Route path="/become-florist" element={<Navigate to="/auth?tab=florist" replace />} />
               <Route path="/search" element={<Search />} />
               <Route path="/florist/:id" element={<FloristProfile />} />
               <Route path="/product/:id/:variantId" element={<ProductDetail />} />
               <Route path="/product/:id/*" element={<ProductDetail />} />
               <Route path="/cart" element={<Cart />} />
               <Route path="/new-cart" element={<NewCart />} />
               <Route path="/guest-order-lookup" element={<GuestOrderLookup />} />
               <Route path="/about" element={<About />} />
               <Route path="/contact" element={<Contact />} />
               <Route path="/faqs" element={<FAQs />} />
               <Route path="/shipping" element={<Shipping />} />
               <Route path="/privacy" element={<Privacy />} />
               <Route path="/terms" element={<Terms />} />
               
               {/* SEO Landing Pages */}
               <Route path="/flowers-sydney" element={<FlowersSydney />} />
               <Route path="/same-day-flower-delivery" element={<SameDayFlowerDelivery />} />
               <Route path="/florists-sydney" element={<FloristsSydney />} />
               <Route path="/local-florists" element={<LocalFlorists />} />
               <Route path="/flower-delivery-sydney" element={<FlowerDeliverySydney />} />
               
               {/* Suburb Pages */}
               <Route path="/florists-newtown" element={<FloristsNewtown />} />
               <Route path="/florists-bondi" element={<FloristsBondi />} />
               <Route path="/florists-surry-hills" element={<FloristsSurryHills />} />
               <Route path="/florists-parramatta" element={<FloristsParramatta />} />
               <Route path="/florists-marrickville" element={<FloristsMarrickville />} />
               
               {/* Blog */}
               <Route path="/blog" element={<BlogIndex />} />
               <Route path="/blog/:slug" element={<BlogPost />} />

               {/* Public checkout flow for guests */}
               <Route path="/delivery-details/:orderId" element={<DeliveryDetails />} />
               <Route path="/order-confirmation" element={<OrderConfirmation />} />
               
               {/* Protected Routes */}
               <Route element={<ProtectedRoute />}>
                 {/* Customer specific routes */}
                 <Route path="/customer-dashboard" element={<CustomerDashboard />} />
                 <Route path="/profile-settings" element={<ProfileSettings />} />
                 <Route path="/order/:id" element={<OrderDetails />} />
                 
                 <Route path="/checkout/processing" element={<CheckoutProcessing />} />
                 <Route path="/checkout/success" element={<CheckoutSuccess />} />

                 {/* Florist specific routes */}
                 <Route path="/florist-dashboard" element={<Dashboard />} />
                 <Route path="/store-settings" element={<StoreSettings />} />
                 <Route path="/florist-dashboard/settings" element={<StoreSettings />} />
                 <Route path="/payment-settings" element={<PaymentSettings />} />
                 <Route path="/florist-dashboard/payment-settings" element={<PaymentSettings />} />
                 <Route path="/florist-dashboard/analytics" element={<FloristAnalytics />} />
                 <Route path="/orders" element={<Orders />} />
                 <Route path="/florist-dashboard/orders" element={<Orders />} />
                 <Route path="/florist-dashboard/orders/:id" element={<FloristOrderDetails />} />
                 <Route path="/products" element={<Products />} />
                 <Route path="/products/new" element={<NewProduct />} />
                 <Route path="/products/edit/:id" element={<EditProduct />} />
                                 <Route path="/florist-dashboard/products" element={<Products />} />
                <Route path="/florist-dashboard/notifications" element={<NotificationsPage />} />
                <Route path="/florist-dashboard/live" element={<LiveDashboard />} />
                <Route path="/florist-dashboard/stripe" element={<FloristStripe />} />
                 
                 {/* Admin specific routes */}
                 <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                 <Route path="/admin/dashboard" element={<AdminDashboard />} />
                 <Route path="/admin/settings" element={<AdminSettings />} />
                 <Route path="/admin/stores" element={<StoreManagement />} />
                 <Route path="/admin/stores/:id" element={<StoreDetails />} />
                 <Route path="/admin/stores/:id/edit" element={<StoreDetails />} />
                 <Route path="/admin/stores/:storeId/products/new" element={<AdminNewProduct />} />
                 <Route path="/admin/stores/:storeId/products/edit/:productId" element={<AdminEditProduct />} />
                 <Route path="/admin/orders" element={<OrderManagement />} />
                 <Route path="/admin/commission" element={<CommissionManagement />} />
                 <Route path="/admin/users" element={<UserManagement />} />
                 <Route path="/admin/customers" element={<CustomerManagement />} />
                 <Route path="/admin/florists" element={<FloristManagement />} />
                 <Route path="/admin/featured" element={<FeaturedContentManagement />} />
               </Route>
               
               <Route path="/orders/:id" element={<OrderRedirect />} /> 
               
               <Route
                 path="*"
                 element={
                   <div className="container mx-auto px-4 py-8">
                     <h1 className="text-3xl font-bold mb-4">404 - Page Not Found</h1>
                     <p className="mb-4">The page you're looking for doesn't exist.</p>
                     <Link
                       to="/"
                       className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/80"
                     >
                       Go Home
                     </Link>
                   </div>
                 }
               />
             </Routes>
           </Suspense>
         </main>
         
         <Footer />
         <Toaster />
         <AdminBypass />
         <CookieBanner />
         <Analytics />
       </div>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App; 
